import { GroupHeaderProps } from "@/src/components/FactionDetail";
import { Codex, Media } from "@/src/payload-types";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CodexEntryCategory } from "@/src/collections/enums";
import { useState } from "react";
import { FaStar } from "react-icons/fa";
import { BiCollapseVertical, BiExpandVertical } from "react-icons/bi";
import RelationLink from "@/src/components/RelationLink";
import { GroupWithSummary } from "@/app/(app)/codex/FactionSelection";

// the fields getCodexFactions selects for the entry lists
export type CodexListEntry = Pick<Codex, "id" | "name" | "slug" | "category" | "isImportant" | "group">;

const CodexFaction = ({
    group,
    splash,
    image,
    entries = [],
}: GroupHeaderProps & {
    entries?: CodexListEntry[];
}) => {
    const groupedEntries = Object.groupBy(entries, (e) => e.category);
    const allCategories = Object.keys(groupedEntries) as CodexEntryCategory[];
    const [openCategories, setOpenCategories] = useState<CodexEntryCategory[]>(() => allCategories);
    const openAll = () => setOpenCategories(allCategories);
    const closeAll = () => setOpenCategories([]);

    return (
        <div>
            <FactionHeader group={group} splash={splash} image={image} />
            <div className="flex justify-center gap-2 mt-4">
                <button className="codex-button flex items-center gap-1" onClick={openAll}>
                    Open All <BiExpandVertical />
                </button>
                <button className="codex-button flex items-center gap-1" onClick={closeAll}>
                    Close All <BiCollapseVertical />
                </button>
            </div>
            <div className="flex gap-2 justify-center">
                {Object.keys(groupedEntries).map((key) => (
                    <CodexCategory
                        key={key}
                        openCategories={openCategories}
                        setOpenCategories={setOpenCategories}
                        category={key as CodexEntryCategory}
                        entries={groupedEntries[key as CodexEntryCategory]!}
                    />
                ))}
            </div>
        </div>
    );
};

const CodexCategory = ({
    category,
    entries,
    openCategories,
    setOpenCategories,
}: {
    category: CodexEntryCategory;
    entries: CodexListEntry[];
    openCategories: CodexEntryCategory[];
    setOpenCategories: React.Dispatch<React.SetStateAction<CodexEntryCategory[]>>;
}) => {
    return (
        <div>
            <Accordion
                type="multiple"
                value={openCategories.includes(category) ? [category] : []}
                onValueChange={(val) =>
                    setOpenCategories((prev) =>
                        val.length > 0
                            ? [...prev.filter((c) => c !== category), category]
                            : prev.filter((c) => c !== category),
                    )
                }
            >
                <AccordionItem value={category}>
                    <AccordionTrigger>{category}</AccordionTrigger>
                    <AccordionContent>
                        {entries.map((e) => (
                            <div className="flex gap-1 items-center hover:cursor-pointer hover:underline" key={e.id}>
                                <RelationLink relationTo={"codex"} id={e.slug} label={e.name} />
                                {/*<span>{e.name}</span>*/}
                                {e.isImportant && (
                                    <span>
                                        <FaStar color="gold" size={12} />
                                    </span>
                                )}
                            </div>
                        ))}
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    );
};

const FactionHeader = ({ group, splash, image }: GroupHeaderProps) => {
    return (
        <div className="flex">
            <div className="basis-1/2 mt-[2em] ml-[2em] mr-[2em]">
                {/*Title*/}
                <div className="">
                    <div className="text-4xl font-bold">{group.name}</div>
                    <div className="text-l">
                        {(group as GroupWithSummary | undefined)?.summary?.summary}
                    </div>
                </div>
            </div>
            <div className="basis-1/2 mr-[2em]">
                {/*cover*/}
                <div className="">
                    {splash && <img src={image} alt={(group.splash as Media)?.alt || ""} className="rounded-xl" />}
                </div>
            </div>
        </div>
    );
};

export default CodexFaction;
